import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api.js'
import { ago, dateTime, humanise } from '../../lib/format.js'
import { Badge, Card, Empty, Field, Notice, RowsSkeleton, Segmented, SettingRow, Switch } from '../../components/ui.js'
import { ConfirmDialog, toast } from '../../components/feedback.js'
import { PlusIcon, XIcon } from '../../components/icons.js'
import { EmailEditor, SlotEditor, defaultSlots } from './Composer.js'
import { MESSAGE_TONE, type TemplatesPayload, type WorkflowConfig, type WorkflowPayload, type WorkflowStep } from './types.js'

/**
 * The automated recovery workflow: up to three reminders after abandonment,
 * each on its own channel, delay and approved template. Saved through the
 * same validation as a campaign, and only checkouts abandoned after it is
 * switched on are ever picked up.
 */

const DELAY_UNITS = [
  { key: 'minutes', label: 'minutes', factor: 1 },
  { key: 'hours', label: 'hours', factor: 60 },
  { key: 'days', label: 'days', factor: 1440 },
] as const

function splitDelay(minutes: number): { amount: number; unit: (typeof DELAY_UNITS)[number]['key'] } {
  if (minutes % 1440 === 0) return { amount: minutes / 1440, unit: 'days' }
  if (minutes % 60 === 0) return { amount: minutes / 60, unit: 'hours' }
  return { amount: minutes, unit: 'minutes' }
}

export function AutomationPanel({ canWrite }: { canWrite: boolean }): ReactNode {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<WorkflowConfig | null>(null)
  const [stepErrors, setStepErrors] = useState<Record<string, string[]>>({})
  const [confirmActivate, setConfirmActivate] = useState(false)

  const workflow = useQuery({ queryKey: ['abandoned-workflow'], queryFn: () => api<WorkflowPayload>('/v1/abandoned/workflow') })
  const templates = useQuery({ queryKey: ['abandoned-templates'], queryFn: () => api<TemplatesPayload>('/v1/abandoned/templates'), staleTime: 120_000 })
  const approved = useMemo(() => (templates.data?.whatsapp.templates ?? []).filter((template) => template.status === 'approved'), [templates.data])

  useEffect(() => {
    if (workflow.data && draft === null) setDraft(workflow.data.config)
  }, [workflow.data, draft])

  const save = useMutation({
    mutationFn: (config: WorkflowConfig) => api<{ status: string; warnings: Record<string, string[]> }>('/v1/abandoned/workflow', { method: 'PUT', body: config }),
    onSuccess: (result) => {
      setStepErrors(result.warnings ?? {})
      setConfirmActivate(false)
      toast('ok', result.status === 'active' ? 'Automation is on' : 'Automation saved', result.status === 'active' ? 'New abandoned checkouts will be followed up automatically.' : 'It is paused; nothing is sent until you switch it on.')
      void queryClient.invalidateQueries({ queryKey: ['abandoned-workflow'] })
    },
    onError: (error) => {
      setConfirmActivate(false)
      if (error instanceof ApiError && error.fields) setStepErrors(error.fields)
      toast('error', 'Not saved', (error as Error).message)
    },
  })

  if (workflow.isPending || !draft) return <Card><RowsSkeleton rows={6} /></Card>
  if (workflow.error) return <Card><Empty error title="Could not load automation">{(workflow.error as Error).message}</Empty></Card>

  const update = (patch: Partial<WorkflowConfig>): void => setDraft({ ...draft, ...patch })
  const updateStep = (index: number, patch: Partial<WorkflowStep>): void => update({ steps: draft.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)) })

  const addStep = (): void => {
    const ids = ['s1', 's2', 's3'] as const
    const id = ids.find((candidate) => !draft.steps.some((step) => step.id === candidate))
    if (!id) return
    const last = draft.steps.at(-1)?.delayMinutes ?? 0
    const starter = templates.data?.email.templates[0]
    update({ steps: [...draft.steps, { id, delayMinutes: Math.max(last * 2, last + 60), channel: 'email', email: { subject: starter?.subject ?? '', body: starter?.body ?? '' } }] })
  }

  const counts = (stepId: string, status: string): number => workflow.data.stepCounts.filter((row) => row.step === stepId && row.status === status).reduce((sum, row) => sum + row.count, 0)

  const submit = (status: WorkflowConfig['status']): void => {
    const next = { ...draft, status }
    setDraft(next)
    save.mutate(next)
  }

  return (
    <div className="stack">
      <Card
        title="Automated recovery"
        subtitle="Reminders sent for you after a checkout is abandoned."
        action={
          <Badge tone={workflow.data.config.status === 'active' ? 'ok' : undefined} dot>
            {workflow.data.config.status === 'active' ? 'On' : 'Off'}
          </Badge>
        }
      >
        <p className="muted small">
          {workflow.data.activatedAt ? `Switched on ${dateTime(workflow.data.activatedAt)} — only checkouts abandoned after that are followed up.` : 'When switched on, only checkouts abandoned from then on are followed up — your existing list is never messaged in bulk.'}
          {workflow.data.lastRunAt ? ` Last checked ${ago(workflow.data.lastRunAt)}.` : ''}
        </p>
        <p className="muted small">Every reminder stops automatically once the shopper completes the purchase, and skips anyone opted out or contacted in the last {draft.cooldownHours} hours on that channel.</p>
      </Card>

      {draft.steps.map((step, index) => {
        const delay = splitDelay(step.delayMinutes)
        const template = approved.find((entry) => entry.name === step.whatsapp?.template)

        return (
          <Card
            key={step.id}
            title={`Reminder ${index + 1}`}
            subtitle={`${counts(step.id, 'sent') + counts(step.id, 'delivered') + counts(step.id, 'read')} sent · ${counts(step.id, 'skipped')} skipped · ${counts(step.id, 'failed')} failed`}
            action={
              canWrite && draft.steps.length > 1 ? (
                <button type="button" className="icon-btn" aria-label={`Remove reminder ${index + 1}`} onClick={() => update({ steps: draft.steps.filter((_, i) => i !== index) })}>
                  <XIcon size={16} />
                </button>
              ) : undefined
            }
          >
            {stepErrors[step.id] && (
              <Notice kind="error" title="This step cannot be sent">
                <ul className="plain-list">{stepErrors[step.id]!.map((error) => <li key={error}>{error}</li>)}</ul>
              </Notice>
            )}
            <fieldset className="step-grid" disabled={!canWrite}>
              <Field label="Send after">
                <span className="row" style={{ gap: 6 }}>
                  <input
                    className="input"
                    style={{ width: 80 }}
                    inputMode="numeric"
                    aria-label="Delay amount"
                    value={delay.amount}
                    onChange={(event) => updateStep(index, { delayMinutes: Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1) * DELAY_UNITS.find((unit) => unit.key === delay.unit)!.factor })}
                  />
                  <select className="select" aria-label="Delay unit" value={delay.unit} onChange={(event) => updateStep(index, { delayMinutes: delay.amount * DELAY_UNITS.find((unit) => unit.key === event.target.value)!.factor })}>
                    {DELAY_UNITS.map((unit) => <option key={unit.key} value={unit.key}>{unit.label}</option>)}
                  </select>
                </span>
              </Field>
              <Field label="Channel">
                <Segmented
                  label="Channel"
                  value={step.channel}
                  onChange={(channel) => {
                    const first = approved[0]
                    const starter = templates.data?.email.templates[0]
                    updateStep(index, channel === 'whatsapp'
                      ? { channel, whatsapp: step.whatsapp ?? (first ? { template: first.name, language: first.language ?? 'en', variables: defaultSlots(first.variables) } : undefined) }
                      : { channel, email: step.email ?? { subject: starter?.subject ?? '', body: starter?.body ?? '' } })
                  }}
                  options={[{ key: 'whatsapp', label: 'WhatsApp' }, { key: 'email', label: 'Email' }]}
                />
              </Field>
              <Field label="Coupon (optional)">
                <input className="input" maxLength={40} value={step.couponCode ?? ''} onChange={(event) => updateStep(index, { couponCode: event.target.value.toUpperCase() || null })} />
              </Field>
            </fieldset>

            {step.channel === 'whatsapp' && templates.data && (
              approved.length === 0 ? (
                <Notice kind="warn" title="No approved WhatsApp templates">Only approved templates can be sent automatically.</Notice>
              ) : (
                <fieldset className="stack--sm" disabled={!canWrite}>
                  <Field label="Approved template">
                    <select
                      className="select"
                      value={step.whatsapp?.template ?? ''}
                      onChange={(event) => {
                        const next = approved.find((entry) => entry.name === event.target.value)
                        if (next) updateStep(index, { whatsapp: { template: next.name, language: next.language ?? 'en', variables: defaultSlots(next.variables) } })
                      }}
                    >
                      {!template && <option value="">Choose…</option>}
                      {approved.map((entry) => <option key={`${entry.name}-${entry.language ?? ''}`} value={entry.name}>{entry.name}</option>)}
                    </select>
                  </Field>
                  {step.whatsapp && <SlotEditor template={template} slots={step.whatsapp.variables} variables={templates.data.variables} onChange={(variables) => updateStep(index, { whatsapp: { ...step.whatsapp!, variables } })} />}
                </fieldset>
              )
            )}
            {step.channel === 'email' && templates.data && step.email && (
              <fieldset disabled={!canWrite}>
                <EmailEditor value={step.email} variables={templates.data.variables} onChange={(email) => updateStep(index, { email })} />
              </fieldset>
            )}
          </Card>
        )
      })}

      {canWrite && draft.steps.length < 3 && (
        <button type="button" className="btn" onClick={addStep}>
          <PlusIcon size={15} /> Add a reminder
        </button>
      )}

      <Card title="Delivery rules">
        <fieldset className="stack" disabled={!canWrite}>
          <SettingRow title="Quiet hours" description="Hold reminders during these hours in the store's timezone; they go out when the window ends.">
            <Switch label="Quiet hours" checked={draft.quietHours.enabled} onChange={(enabled) => update({ quietHours: { ...draft.quietHours, enabled } })} />
          </SettingRow>
          {draft.quietHours.enabled && (
            <div className="grid-3">
              <Field label="From">
                <input className="input" type="time" value={draft.quietHours.start} onChange={(event) => update({ quietHours: { ...draft.quietHours, start: event.target.value } })} />
              </Field>
              <Field label="Until">
                <input className="input" type="time" value={draft.quietHours.end} onChange={(event) => update({ quietHours: { ...draft.quietHours, end: event.target.value } })} />
              </Field>
              <Field label="Timezone">
                <input className="input" value={draft.quietHours.timezone} onChange={(event) => update({ quietHours: { ...draft.quietHours, timezone: event.target.value } })} />
              </Field>
            </div>
          )}
          <div className="grid-3">
            <Field label="Retries on provider outage">
              <select className="select" value={draft.maxRetries} onChange={(event) => update({ maxRetries: Number(event.target.value) })}>
                {[0, 1, 2, 3, 5].map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="Gap between messages on one channel (hours)">
              <input className="input" inputMode="numeric" value={draft.cooldownHours} onChange={(event) => update({ cooldownHours: Math.min(168, Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1)) })} />
            </Field>
          </div>
        </fieldset>
      </Card>

      {canWrite && (
        <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" disabled={save.isPending} onClick={() => submit('paused')}>
            {draft.status === 'active' ? 'Pause automation' : 'Save as paused'}
          </button>
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => (workflow.data.config.status === 'active' ? submit('active') : setConfirmActivate(true))}>
            {workflow.data.config.status === 'active' ? 'Save changes' : 'Save & switch on'}
          </button>
        </div>
      )}

      <Card flush title="Recent automated messages">
        {workflow.data.history.length === 0 ? (
          <Empty title="Nothing sent automatically yet">Messages appear here as the workflow runs.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Checkout</th>
                  <th>Step</th>
                  <th>Channel</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {workflow.data.history.map((row) => (
                  <tr key={row.id}>
                    <td>{dateTime(row.at)}</td>
                    <td>#{row.checkoutId ?? '—'}</td>
                    <td>{row.step?.replace('s', 'Reminder ') ?? '—'}</td>
                    <td>{row.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}</td>
                    <td>
                      <Badge tone={MESSAGE_TONE[row.status]}>{humanise(row.status)}</Badge>
                      {row.reason && <span className="table__sub">{row.reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmActivate}
        title="Switch automated recovery on?"
        confirmLabel="Switch on"
        pending={save.isPending}
        onClose={() => setConfirmActivate(false)}
        onConfirm={() => submit('active')}
        message="From now on, each newly abandoned checkout is sent these reminders on schedule. WhatsApp messages are charged per message from your wallet. Checkouts abandoned before now are not messaged."
      />
    </div>
  )
}
